import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getSessionAndMemberStatus } from "@/lib/authz";

const codeExchangePromises = new Map();

function exchangeCodeOnce(code) {
  if (!codeExchangePromises.has(code)) {
    codeExchangePromises.set(
      code,
      supabase.auth.exchangeCodeForSession(code)
    );
  }

  return codeExchangePromises.get(code);
}

async function waitForSession(timeoutMs = 10000) {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) throw error;
  if (session) return session;

  return new Promise((resolve, reject) => {

    let subscription;

    const timeout = window.setTimeout(() => {
      subscription?.unsubscribe();
      reject(new Error("The sign-in link could not be completed."));
    }, timeoutMs);

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!newSession) return;

      window.clearTimeout(timeout);
      subscription?.unsubscribe();
      resolve(newSession);
    });

    subscription = data.subscription;
  });
}

async function establishSession(code) {
  if (code) {
    const { data, error } = await exchangeCodeOnce(code);

    if (error) throw error;
    if (data?.session) return data.session;
  }

  return waitForSession();
}

function getCallbackError(params) {
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, "")
  );

  return (
    params.get("error_description") ||
    hashParams.get("error_description") ||
    params.get("error") ||
    hashParams.get("error") ||
    ""
  );
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get("code");
  const trionfiResult = params.get("trionfi_result");
  const isInvite = params.get("invite") === "1";
  const callbackError = getCallbackError(params);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (callbackError) {
          throw new Error(callbackError);
        }

        const session = await establishSession(code);
        if (!alive) return;

        if (!session) {
          navigate("/signin", { replace: true });
          return;
        }

        const isAnon =
          session.user?.is_anonymous === true ||
          session.user?.user_metadata?.is_anonymous === true;

        if (isAnon) {
          await supabase.auth.signOut();
          navigate("/signin?error=anon_not_allowed", { replace: true });
          return;
        }

        /*
         * A Trionfi claim creates the active membership. It must
         * therefore be verified before the usual member check.
         */
        if (trionfiResult) {
          const { error: claimError } = await supabase.rpc(
            "verify_trionfi_claim",
            {
              p_result_id: trionfiResult,
            }
          );

          if (claimError) throw claimError;
          if (!alive) return;

          navigate("/members", { replace: true });
          return;
        }

        const { isMember } = await getSessionAndMemberStatus();
        if (!alive) return;

        if (isMember) {
          navigate("/members", { replace: true });
        } else {
          navigate(isInvite ? "/requestaccess?invite=1" : "/requestaccess", {
            replace: true,
          });
        }
      } catch (e) {
        console.error(e);
        navigate("/signin?error=callback", { replace: true });
      }
    })();

    return () => { alive = false; };
  }, [callbackError, code, isInvite, navigate, trionfiResult]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <p className="font-sans text-base opacity-80">Signing you in…</p>
    </div>
  );
}